/**
 * Общий макет занятия: текст упражнения первым (ТЗ FR-EN-84).
 *
 * Все режимы были устроены одинаково: карточка с рассказом о задании, а под
 * ней — экран набора. Рассказ занимал целый экран, и до текста, ради которого
 * человек пришёл, приходилось листать. При этом рассказ читают один раз, а
 * текст — каждую секунду занятия.
 *
 * Теперь над текстом стоит одна строка: название режима, суть задания сейчас и
 * прогресс. Всё остальное — открытые клавиши, подсказки о Shift, порядок шагов,
 * сколько осталось — раскрывается под текстом по требованию. Ничего не убрано:
 * убрана только обязанность пролистать это, чтобы начать.
 *
 * В фокус-режиме от макета не остаётся ничего, кроме набора (ТЗ FR-EN-34).
 */

import type { ReactNode } from 'react';
import type { Translate } from '@type2x/i18n';
import { ProgressBar } from '@type2x/ui';

import { useFocusMode } from './focus-mode.js';

export interface DrillLayoutProps {
  readonly t: Translate;
  /** Название режима: то же, что стояло заголовком карточки. */
  readonly title: string;
  /**
   * Суть задания прямо сейчас: что осваиваем, какое слово разбираем, какая
   * цель у прогона. Строка, а не абзац: её читают взглядом, не отрываясь от
   * текста упражнения.
   */
  readonly now?: ReactNode;
  /** Прогресс режима; `undefined` — режиму нечего считать. */
  readonly progress?: { readonly value: number; readonly label: string } | undefined;
  /** Всё остальное о задании: раскрывается под текстом. */
  readonly details?: ReactNode;
  /** Экран набора. */
  readonly children: ReactNode;
}

export function DrillLayout({
  t,
  title,
  now,
  progress,
  details,
  children,
}: DrillLayoutProps): React.JSX.Element {
  const focusMode = useFocusMode();

  /*
   * Порядок и число мест в разметке одинаковы в обоих режимах.
   *
   * Раньше фокус-режим возвращал другое дерево, и экран набора переезжал с
   * места на место: React сверяет детей по порядку, поэтому включение режима
   * посреди упражнения могло пересоздать экран вместе с измерением
   * (ТЗ UI-48 — «измерение не перезапускается»). Скрытое место остаётся
   * местом, и этого достаточно.
   */
  return (
    <section className="t2x-drill">
      {!focusMode && (
        <div className="t2x-drill__bar">
          <h2 className="t2x-drill__title">{title}</h2>
          {/* Не абзац, а блок: внутри бывает список подсказок о Shift, а
              список внутри абзаца — недопустимая вложенность, о которой React
              предупреждает про ошибку гидратации. */}
          {now !== undefined && <div className="t2x-drill__now">{now}</div>}
          {progress !== undefined && <ProgressBar value={progress.value} label={progress.label} />}
        </div>
      )}

      {children}

      {!focusMode && details !== undefined && (
        <details className="t2x-drill__more">
          <summary>{t('drill.details')}</summary>
          <div className="t2x-drill__details">{details}</div>
        </details>
      )}
    </section>
  );
}
